import { useLayoutEffect } from 'react';

import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRefundEligibility } from 'hooks/refund-eligibility/use-refund-eligibility';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { routes } from 'utils/routing';

export interface IUseRedirectToSupportFormIfRefundIneligibleInput {
  orderId: string;
  categoryId: string;
  subcategoryId: string;
}

/**
 * This hook redirects the customer to the support flow if the order passed in is not eligible for a refund
 */
export const useRedirectToSupportFormIfRefundIneligible = ({
  orderId,
  categoryId,
  subcategoryId,
}: IUseRedirectToSupportFormIfRefundIneligibleInput) => {
  const {
    isDeterminingRefundEligibility,
    isOrderEligibleForRefund,
    refundIneligibilityReason,
  } = useRefundEligibility({ orderId });
  const { navigate } = useNavigation();
  const enableGuestAndMultiStepSupportForm = useFlag(
    LaunchDarklyFlag.ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE
  );

  /**
   * useLayoutEffect can prevent the component consuming this hook from rendering before redirecting,
   * eliminating a distracting flash of unintended content from showing
   */
  useLayoutEffect(() => {
    if (isDeterminingRefundEligibility) {
      return;
    }

    if (!isOrderEligibleForRefund && categoryId && subcategoryId) {
      const newQuery = new URLSearchParams({
        orderId,
        categoryId,
        subcategoryId,
      }).toString();
      // multistep support form allows including an orderId with submission
      const url = enableGuestAndMultiStepSupportForm
        ? `${routes.supportForm}?${newQuery}`
        : routes.support;

      navigate(url, {
        state: { refundIneligibilityReason },
      });
    }
  }, [
    categoryId,
    enableGuestAndMultiStepSupportForm,
    isDeterminingRefundEligibility,
    isOrderEligibleForRefund,
    navigate,
    orderId,
    refundIneligibilityReason,
    subcategoryId,
  ]);

  return { isDeterminingRefundEligibility };
};
